import { MOSAIC_PX, TILE_PX, type TilePin, type TileRef, pinWithinMosaic, tileKey } from '@/capture/stampTile';
import type { MosaicView } from '@/capture/useStampTile';
import { useT } from '@/i18n/runtime';
import { Icon } from './Icon';

/**
 * The expanded map (the owner's "show maps like the GPS Camera app"): a full-width sheet over the viewfinder built
 * from the 3×3 mosaic of the be's own tiles around the fix — the fix pin, the registered-address marker when it lies
 * inside the mosaic (else an edge arrow saying which way, with the same distance words), the plain-words reading
 * "You are here · about 44 m from the registered address · within 50 m", a close control. NO pan or zoom in this cut —
 * the sheet says so. A view only: nothing here is bound, chained or sent, and the bar below stays tappable.
 */
export function StampMapSheet({
  fix,
  centre,
  mosaic,
  distanceWords,
  onClose,
}: {
  fix: { readonly lat: number; readonly lng: number };
  centre: TileRef;
  mosaic: MosaicView;
  /** The card's own distance line (the be's band words), or null when the tenant turned the band off. */
  distanceWords: string | null;
  onClose: () => void;
}): JSX.Element {
  const { t } = useT();
  const pin: TilePin | null = pinWithinMosaic(centre, fix.lat, fix.lng);
  const pct = (px: number): string => `${(px / MOSAIC_PX) * 100}%`;
  const drawn = mosaic.tiles.filter((x) => x.url !== null).length;
  // a real <dialog open> (the semantic element carries role "dialog"); UA styles are reset in svapp.css
  return (
    <dialog open className="sv-map-sheet" aria-modal="true" aria-label={t('sv.capture.stamp.map.title')} data-testid="stamp-map-sheet" data-status={mosaic.status}>
      <div className="sv-map-head">
        <span className="sv-map-title">
          <Icon name="mappin" size={14} /> {t('sv.capture.stamp.map.title')}
        </span>
        <button type="button" className="sv-pill" onClick={onClose} aria-label={t('sv.capture.stamp.map.close')} data-testid="stamp-map-close">
          ✕
        </button>
      </div>
      <div className="sv-map-grid" data-testid="stamp-map-grid" data-tiles-drawn={drawn} style={{ gridTemplateColumns: `repeat(${Math.round(MOSAIC_PX / TILE_PX)}, 1fr)` }}>
        {mosaic.tiles.map((cell) => (
          <div key={tileKey(cell.ref)} className="sv-map-cell" data-testid="stamp-map-tile" data-tile={tileKey(cell.ref)}>
            {cell.url ? <img className="sv-map-img" src={cell.url} alt="" /> : <span className="sv-map-gap" aria-hidden="true" />}
          </div>
        ))}
        {pin ? <span className="sv-map-pin" data-testid="stamp-map-pin" style={{ left: pct(pin.x), top: pct(pin.y) }} aria-hidden="true" /> : null}
        {mosaic.anchorPin ? (
          <span className="sv-map-anchor" data-testid="stamp-map-anchor" style={{ left: pct(mosaic.anchorPin.x), top: pct(mosaic.anchorPin.y) }} aria-hidden="true" />
        ) : mosaic.edge ? (
          <span className={`sv-map-edge is-${mosaic.edge}`} data-testid="stamp-map-edge" data-side={mosaic.edge} aria-hidden="true">
            <Icon name="skip" size={16} />
          </span>
        ) : null}
      </div>
      <p className="sv-map-words" data-testid="stamp-map-words">
        <b>{t('sv.capture.stamp.map.here')}</b>
        {distanceWords ? ` · ${distanceWords}` : ''}
      </p>
      {mosaic.edge ? (
        <p className="sv-map-note" data-testid="stamp-map-edge-words">
          {t('sv.capture.stamp.map.anchorOff', { side: t(`sv.capture.stamp.map.side.${mosaic.edge}`) })}
        </p>
      ) : null}
      {mosaic.status === 'error' ? <p className="sv-map-note" data-testid="stamp-map-note">{t('sv.capture.stamp.map.unavailable')}</p> : null}
      <p className="sv-map-fixed">{t('sv.capture.stamp.map.fixed')}</p>
    </dialog>
  );
}
